import Debug from 'debug'
import moize from 'moize'
import { invert } from 'remeda'
import { t } from '@lingui/core/macro'

import { ItemKind } from '@jotta/grpc-web/no/jotta/openapi/file/file.v2_pb'
import type { BrandThemeIcon } from '@jotta/types/Brand'
import SvgDevice from '@jotta/ui/SvgDevice'
import SvgFile from '@jotta/ui/SvgFile'
import SvgFileArchive from '@jotta/ui/SvgFileArchive'
import SvgFileAudio from '@jotta/ui/SvgFileAudio'
import SvgFileDoc from '@jotta/ui/SvgFileDoc'
import SvgFileHtml from '@jotta/ui/SvgFileHtml'
import SvgFilePdf from '@jotta/ui/SvgFilePdf'
import SvgFilePpt from '@jotta/ui/SvgFilePpt'
import SvgFileText from '@jotta/ui/SvgFileText'
import SvgFileVideo from '@jotta/ui/SvgFileVideo'
import SvgFileXls from '@jotta/ui/SvgFileXls'
import SvgFolderFolderLight from '@jotta/ui/SvgFolderFolderLight'
import SvgFolderSharedFolderLight from '@jotta/ui/SvgFolderSharedFolderLight'
import SvgImageSquare from '@jotta/ui/SvgImageSquare'
import SvgSpreadsheet from '@jotta/ui/SvgSpreadsheet'

const debug = Debug('jotta:types:FileItemType')

export type ItemKindKey = keyof typeof ItemKind
export type FileItemTypeConfig = {
  [K in keyof typeof ItemKind & string]: {
    label: string
    icon: BrandThemeIcon
    kind: (typeof ItemKind)[K]
  }
}

const ItemKindObj: Record<ItemKindKey, ItemKind> = { ...ItemKind }
const ItemKindReverse: Record<ItemKind, ItemKindKey> = invert(ItemKindObj)
export const getItemKindKey = moize(
  (kind: ItemKind = ItemKind.UNKNOWN_ITEM_KIND): ItemKindKey =>
    ItemKindReverse[kind] || 'UNKNOWN_ITEM_KIND',
)

debug('keys', ItemKindReverse)

export const fileKindConfig: FileItemTypeConfig = {
  ANDROID: {
    get label() {
      return t`Phone`
    },
    icon: SvgDevice,
    kind: ItemKind.ANDROID,
  },
  ARCHIVE_KIND: {
    get label() {
      return t`Archive`
    },
    icon: SvgFileArchive,
    kind: ItemKind.ARCHIVE_KIND,
  },
  AUDIO: {
    get label() {
      return t`Audio`
    },
    icon: SvgFileAudio,
    kind: ItemKind.AUDIO,
  },
  CLI: {
    get label() {
      return t`Command-line`
    },
    icon: SvgDevice,
    kind: ItemKind.CLI,
  },
  COLLABORATION_FOLDER: {
    get label() {
      return t`Folder`
    },
    icon: SvgFolderSharedFolderLight,
    kind: ItemKind.COLLABORATION_FOLDER,
  },
  DOCUMENT: {
    get label() {
      return t`Document`
    },
    icon: SvgFileText,
    kind: ItemKind.DOCUMENT,
  },
  EXCEL: {
    get label() {
      return t`Excel`
    },
    icon: SvgFileXls,
    kind: ItemKind.EXCEL,
  },
  HTML: {
    get label() {
      return t`HTML`
    },
    icon: SvgFileHtml,
    kind: ItemKind.HTML,
  },
  IMAC: {
    get label() {
      return t`Desktop`
    },
    icon: SvgDevice,
    kind: ItemKind.IMAC,
  },
  IMAGE: {
    get label() {
      return t`Image`
    },
    icon: SvgImageSquare,
    kind: ItemKind.IMAGE,
  },
  IPAD: {
    get label() {
      return t`Tablet`
    },
    icon: SvgDevice,
    kind: ItemKind.IPAD,
  },
  IPHONE: {
    get label() {
      return t`Phone`
    },
    icon: SvgDevice,
    kind: ItemKind.IPHONE,
  },
  LAPTOP: {
    get label() {
      return t`Laptop`
    },
    icon: SvgDevice,
    kind: ItemKind.LAPTOP,
  },
  MACBOOK: {
    get label() {
      return t`Laptop`
    },
    icon: SvgDevice,
    kind: ItemKind.MACBOOK,
  },
  MACPRO: {
    get label() {
      return t`Desktop`
    },
    icon: SvgDevice,
    kind: ItemKind.MACPRO,
  },
  MODULE: {
    get label() {
      return t`Module`
    },
    icon: SvgFolderFolderLight,
    kind: ItemKind.MODULE,
  },
  OTHER: {
    get label() {
      return t`Other`
    },
    icon: SvgFile,
    kind: ItemKind.OTHER,
  },
  PDF: {
    get label() {
      return t`PDF`
    },
    icon: SvgFilePdf,
    kind: ItemKind.PDF,
  },
  POWERPOINT: {
    get label() {
      return t`Powerpoint`
    },
    icon: SvgFilePpt,
    kind: ItemKind.POWERPOINT,
  },
  PRESENTATION: {
    get label() {
      return t`Presentation`
    },
    icon: SvgFileText,
    kind: ItemKind.PRESENTATION,
  },
  REGULAR_FOLDER: {
    get label() {
      return t`Folder`
    },
    icon: SvgFolderFolderLight,
    kind: ItemKind.REGULAR_FOLDER,
  },
  RICH_TEXT: {
    get label() {
      return t`Text`
    },
    icon: SvgFileText,
    kind: ItemKind.RICH_TEXT,
  },
  SPREADSHEET: {
    get label() {
      return t`Spreadsheet`
    },
    icon: SvgSpreadsheet,
    kind: ItemKind.SPREADSHEET,
  },
  TEXT: {
    get label() {
      return t`Text`
    },
    icon: SvgFileText,
    kind: ItemKind.TEXT,
  },
  UNKNOWN_ITEM_KIND: {
    get label() {
      return t`Unknown file`
    },
    icon: SvgFile,
    kind: ItemKind.UNKNOWN_ITEM_KIND,
  },
  VIDEO: {
    get label() {
      return t`Video`
    },
    icon: SvgFileVideo,
    kind: ItemKind.VIDEO,
  },
  WINDOWS_PHONE: {
    get label() {
      return t`Phone`
    },
    icon: SvgDevice,
    kind: ItemKind.WINDOWS_PHONE,
  },
  WINDOWS_TABLET: {
    get label() {
      return t`Tablet`
    },
    icon: SvgDevice,
    kind: ItemKind.WINDOWS_TABLET,
  },
  WORD: {
    get label() {
      return t`Word`
    },
    icon: SvgFileDoc,
    kind: ItemKind.WORD,
  },
  WORKSTATION: {
    get label() {
      return t`Desktop`
    },
    icon: SvgDevice,
    kind: ItemKind.WORKSTATION,
  },
}

export function getFileKindConfig(kind: ItemKind) {
  return fileKindConfig[ItemKindReverse[kind]]
}
export function getFileKindLabel(kind: ItemKind) {
  return getFileKindConfig(kind).label
}
export const getFileKindIcon = moize(function getFileKindIcon(kind: ItemKind) {
  return getFileKindConfig(kind).icon
})
